import type { FocalStep } from "@archlang/scene/board";
import { Button, useContextMenu, type ContextMenuItem } from "@archlang/ui";
import { displayLabel } from "../displayLabel.js";
import { useTranslation } from "../i18n";

interface BreadcrumbProps {
  readonly steps: readonly FocalStep[];
  readonly onNavigate: (index: number) => void;
}

export function Breadcrumb({ steps, onNavigate }: BreadcrumbProps) {
  const { t } = useTranslation("breadcrumb");
  const menu = useContextMenu();
  return (
    <nav className="flex items-center gap-1 text-sm whitespace-nowrap" aria-label={t("focalPath")}>
      {steps.map((step, i) => {
        // Real model labels (content) are humanized; the root fallback (chrome) is not.
        const label = step.containerItem?.label
          ? displayLabel(step.containerItem.label)
          : t("root");
        const isLast = i === steps.length - 1;
        const menuItems: ContextMenuItem[] = [
          { label: t("goToSpace"), onClick: () => onNavigate(i) },
          { label: t("copyName"), onClick: () => void navigator.clipboard?.writeText(label) },
        ];
        return (
          <div key={step.space.id} className="flex items-center gap-1">
            {i > 0 && <span className="text-slate-600">/</span>}
            <Button
              variant="ghost"
              size="xs"
              onClick={() => onNavigate(i)}
              onContextMenu={(e) => menu.open(e, menuItems)}
              // Current-level click re-frames (= home/reset at that level).
              // Higher levels navigate up to that level.
              title={
                isLast
                  ? t("reCenter")
                  : t("navigateTo", { label })
              }
              className={
                // pointer-coarse: finger-sized targets (~40px tall with the pill).
                "pointer-coarse:h-auto pointer-coarse:px-3 pointer-coarse:py-2 " +
                (isLast ? "text-white" : "")
              }
            >
              {label}
            </Button>
          </div>
        );
      })}
      {menu.node}
    </nav>
  );
}
